/**
 * Group Data Fetching Hooks
 *
 * Hooks for fetching application groups for view permissions.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useAuthFetch } from "../../../utils/useAuthFetch";

const GROUPS_API_BASE = "/api/workspace/myvault/groups";

/**
 * Helper to create headers with workspace ID
 */
const getWorkspaceHeaders = (workspaceId: string): Record<string, string> => ({
  "x-workspace-id": workspaceId,
});

/**
 * Application group type
 */
export interface ApplicationGroup {
  id: string;
  name: string;
  display_name: string;
  description?: string | null;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Hook to fetch all groups for a workspace.
 */
export const useGroups = (workspaceId: string) => {
  const [groups, setGroups] = useState<ApplicationGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const fetchGroups = useCallback(async () => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await authFetchRef.current(
        GROUPS_API_BASE,
        "fetch groups",
        { headers: getWorkspaceHeaders(workspaceId) },
      );
      setGroups(response.items || response || []);
      setError(null);
    } catch (err) {
      console.error("Groups fetch error:", err);
      const message =
        err instanceof Error ? err.message : "Failed to fetch groups";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  return {
    groups,
    loading,
    error,
    refresh: fetchGroups,
  };
};
